import { managementReportFilterGridSizes } from "@common/components/managementReport/managementReportTableSection/ManagementReportTableSection";
import { DotLegalMultiSelect, DotLegalTooltip, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import { useTranslation } from "../../../localization/useTranslation";
import { Box, TableCell, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { LegalEntityAuditGroupAuditModel, LegalEntityAuditGroupAuditTableModel } from "../../legalEntityAuditGroupOverview/LegalEntityAuditGroupOverview.types";
import { useManagementReportExcludedAuditsHooks } from "./ManagementReportExcludedAudits.hooks";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink";
import { useUrlProvider } from "../../../useUrlProvider";
import SystemLinks from "../../../system/SystemLinks";
import QuestionnaireStatus from "../../../common/components/questionnaireComponents/questionnaireStatus/QuestionnaireStatus";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { LegalEntityContactType } from "../../../legalEntity/legalEntityContactTab/LegalEntityContactTab.types";
import { LegalEntityAuditTabs } from "../../../legalEntity/LegalEntity.types";
import QuestionnaireEvaluation from "@common/components/questionnaireComponents/questionnaireEvaluation/QuestionnaireEvaluation.tsx";
import ManagementReportTableSection from "@common/components/managementReport/managementReportTableSection/ManagementReportTableSection";

export interface IManagementReportExcludedAudits {
    isLoading: boolean;
    audits: Array<LegalEntityAuditGroupAuditModel>;
    isPdf: boolean;
}

function ManagementReportExcludedAudits(props: IManagementReportExcludedAudits) {
    const { getLegalEntityUrl } = useUrlProvider();
    const { translateString, translateLegalEntityAuditStatus } = useTranslation();
    const { rows, searchedStatus, setSearchedStatus, exclusionFilterOptions } = useManagementReportExcludedAuditsHooks(props);

    function getHeaders() {
        let headers = Array<ITableHeader<LegalEntityAuditGroupAuditTableModel>>();
        headers.push({ property: "legalEntityName", text: translateString("legalEntity"), align: "left", showOnMobile: true });
        headers.push({ property: "assets", text: translateString("assets"), align: "left", showOnMobile: true });
        headers.push({ property: "contact", text: translateString("contact"), align: "left", showOnMobile: true });
        headers.push({ property: "status", text: translateString("exclusionReason"), align: "left", showOnMobile: true });
        headers.push({ property: "evaluationStatus", text: translateString("evaluation"), align: "left", showOnMobile: true });

        if (props.isPdf) {
            headers.push({ property: "evaluation", text: translateString("evaluationText"), align: "left", showOnMobile: true });
        }

        // Hidden headers - allowing search
        headers.push({ property: "assetsString", text: "", align: "left", showOnMobile: true, hideHeader: true });
        headers.push({ property: "auditStatusString", text: "", align: "left", showOnMobile: true, hideHeader: true });
        headers.push({ property: "contactTypesString", text: "", align: "left", showOnMobile: true, hideHeader: true });

        return headers;
    }

    function getSearchFields() {
        return (
            <React.Fragment>
                <Grid size={managementReportFilterGridSizes}>
                    <DotLegalMultiSelect
                        onChange={setSearchedStatus}
                        chosenOptions={searchedStatus}
                        label={translateString("exclusionReason")}
                        placeholder={translateString("exclusionReason")}
                        isLoading={props.isLoading}
                        options={exclusionFilterOptions}
                        noMargin
                        limit={1}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
            </React.Fragment>
        );
    }

    return (
        <ManagementReportTableSection
            title={translateString("excluded")}
            count={props.audits.length}
            isPdf={props.isPdf}
            headers={getHeaders()}
            defaultOrderBy={"legalEntityName"}
            data={mapToTableData(rows)}
            searchFields={getSearchFields()}
            renderRow={(row) => {
                return (
                    <TableRow key={row.legalEntityId}>
                        <TableCell>
                            <Box key={row.id} component={"span"} sx={{ fontWeight: "bold" }}>
                                <DotLegalLink linkColor={"primary"} to={`${getLegalEntityUrl(row.legalEntityId, LegalEntityAuditTabs.Audits)}`}>
                                    {row.legalEntityName}
                                </DotLegalLink>
                            </Box>
                        </TableCell>
                        <TableCell>
                            <SystemLinks systems={row.assets} />
                        </TableCell>
                        <TableCell>
                            <DotLegalTooltip text={row.contactEmail}>
                                <Box>
                                    {row.contact} {row.contactTypes.length > 0 && <>({row.contactTypesString})</>}
                                </Box>
                            </DotLegalTooltip>
                        </TableCell>
                        <TableCell>
                            <QuestionnaireStatus
                                showQuestionsStatistics={row.showQuestionsStatistics}
                                numberOfQuestionsAnswered={row.numberOfQuestionsAnswered}
                                totalNumberOfQuestions={row.totalNumberOfQuestions}
                                status={row.status}
                                lastestRecipientUpdate={row.lastestResponseChange}
                            />
                        </TableCell>
                        <TableCell>
                            <QuestionnaireEvaluation evaluationStatus={row.evaluationStatus} evaluation={row.evaluation} />
                        </TableCell>
                        {props.isPdf && <TableCell>{row.evaluation}</TableCell>}
                    </TableRow>
                );
            }}
        />
    );

    function mapToTableData(audits: Array<LegalEntityAuditGroupAuditModel> | undefined): Array<LegalEntityAuditGroupAuditTableModel> {
        if (audits) {
            return audits.map((x) => {
                return {
                    ...x,
                    assetsString: x.assets.flatMap((x) => x.name).join(", "),
                    auditStatusString: translateLegalEntityAuditStatus(x.status.toString()),
                    contactTypesString: x.contactTypes.map((x) => translateString(setFirstLetterToLowerCase(LegalEntityContactType[x].toString()))).join(", "),
                };
            });
        }

        return [];
    }
}

export default ManagementReportExcludedAudits;
